import { apiInvoker } from '../../lib/apiInvoker';
import { API_URLS } from '../../lib/apiUrls';
import { 
  generateResearchReportStart,
  generateResearchReportSuccess,
  generateResearchReportFailure,
  clearResearchReport,
  getResearchReportStart,
  getResearchReportSuccess,
  getResearchReportFailure,
  generateOutlineStart,
  generateOutlineSuccess,
  generateOutlineFailure,
  getOutlineStart,
  getOutlineSuccess,
  getOutlineFailure,
  generateScriptStart,
  generateScriptSuccess,
  generateScriptFailure,
  deleteHookStart,
  deleteHookSuccess,
  deleteHookFailure,
  updateHookStart,
  updateHookSuccess,
  updateHookFailure,
} from '../slices/researchReportSlice';
import { handleApiError } from '../../utils/errorHandler';

export const generateResearchReport = (formData) => {
  return async (dispatch) => {
    try {
      dispatch(generateResearchReportStart());
      
      const referenceLinks = Array.isArray(formData.referenceLinks) 
        ? formData.referenceLinks.filter(link => link && link.trim() !== '')
        : [];

      const sortByMap = {
        'Popularity': 'viewCount',
        'Recent': 'date',
        'Views': 'viewCount',
      };

      const requestBody = {
        idea: formData.idea,
        referenceVideoLinks: referenceLinks.map(url => ({ url })),
        region: formData.region,
        competitorChannels: Array.isArray(formData.competitor) 
          ? formData.competitor.map(url => ({ url: url }))
          : formData.competitor 
          ? [{ url: formData.competitor }]
          : [],
        filters: {
          category: formData.category,
          dateRange: parseInt(formData.dateRange.match(/\d+/)?.[0] || '30'),
          sortBy: sortByMap[formData.sortBy] || 'viewCount',
        },
      };
      
      const url = API_URLS.USER.GENERATE_RESEARCH_REPORT;
      const response = await apiInvoker.post(url, requestBody, {
        timeout: 600000 
      });
      if (response?.success && response?.data) {
        dispatch(generateResearchReportSuccess(response.data));
        return response.data;
      }
      if (response) {
        dispatch(generateResearchReportSuccess(response));
        return response;
      }
      
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Research report generation error:', error);
      
      const { error: formattedError, message: errorMessage } = handleApiError(error, {
        defaultMessage: 'Failed to generate research report',
        customMessages: {
          500: 'Server error occurred while generating the research report. Please try again later or contact support if the issue persists.',
          408: 'Request timed out. The research report generation is taking longer than expected. Please try again.',
        },
      });
      
      dispatch(generateResearchReportFailure(errorMessage));
      throw formattedError;
    }
  };
};

export const getResearchReport = (researchReportId) => {
  return async (dispatch) => {
    try {
      dispatch(getResearchReportStart());
      
      const url = API_URLS.USER.GET_RESEARCH_REPORT(researchReportId);
      const response = await apiInvoker.get(url);
      
      if (response?.success && response?.data) {
        dispatch(getResearchReportSuccess(response.data));
        return response.data;
      }
      if (response) {
        dispatch(getResearchReportSuccess(response));
        return response;
      }
      
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Get research report error:', error);
      
      const { error: formattedError, message: errorMessage } = handleApiError(error, {
        defaultMessage: 'Failed to fetch research report',
        customMessages: {
          404: 'Research report not found',
          500: 'Server error occurred while fetching the research report. Please try again later.',
        },
      });
      
      dispatch(getResearchReportFailure(errorMessage));
      throw formattedError;
    }
  };
};

export const generateOutline = (researchReportId, topicId) => {
  return async (dispatch) => {
    try {
      dispatch(generateOutlineStart());
      
      const url = API_URLS.USER.GENERATE_OUTLINE(researchReportId);
      const requestBody = {
        topicId: topicId,
      };
      
      const response = await apiInvoker.post(url, requestBody, {
        timeout: 600000 
      });
      
      if (response?.success && response?.data) {
        const outlineData = {
          outlineId: response.data.outlineId,
          scripts: response.data.scripts || [],
        };
        dispatch(generateOutlineSuccess(outlineData));
        return outlineData;
      }
      if (response?.outlineId) {
        const outlineData = {
          outlineId: response.outlineId,
          scripts: response.scripts || [],
        };
        dispatch(generateOutlineSuccess(outlineData));
        return outlineData;
      }
      
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Generate outline error:', error);
      
      const { error: formattedError, message: errorMessage } = handleApiError(error, {
        defaultMessage: 'Failed to generate outline',
        customMessages: {
          500: 'Server error occurred while generating the outline. Please try again later or contact support if the issue persists.',
          408: 'Request timed out. The outline generation is taking longer than expected. Please try again.',
        },
      });
      
      dispatch(generateOutlineFailure(errorMessage));
      throw formattedError;
    }
  };
};

export const getOutline = (outlineId) => {
  return async (dispatch) => {
    try {
      dispatch(getOutlineStart());
      
      const url = API_URLS.USER.GET_OUTLINE(outlineId);
      const response = await apiInvoker.get(url);
      
      if (response?.success && response?.data) {
        const outlineData = {
          outlineId: response.data.outlineId,
          scripts: response.data.scripts || [],
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt,
        };
        dispatch(getOutlineSuccess(outlineData));
        return outlineData;
      }
      if (response?.outlineId) {
        const outlineData = {
          outlineId: response.outlineId,
          scripts: response.scripts || [],
          createdAt: response.createdAt,
          updatedAt: response.updatedAt,
        };
        dispatch(getOutlineSuccess(outlineData));
        return outlineData;
      }
      
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Get outline error:', error);
      
      const { error: formattedError, message: errorMessage } = handleApiError(error, {
        defaultMessage: 'Failed to fetch outline',
        customMessages: {
          404: 'Outline not found',
          500: 'Server error occurred while fetching the outline. Please try again later.',
        },
      });
      
      dispatch(getOutlineFailure(errorMessage));
      throw formattedError;
    }
  };
};

export const generateScript = (outlineId, hookIds, imageFile) => {
  return async (dispatch) => {
    try {
      dispatch(generateScriptStart());
      
      const url = API_URLS.USER.GENERATE_SCRIPT(outlineId);
      
      console.log('📤 Generating script request:', {
        url,
        outlineId,
        hookIds,
        hasImage: !!imageFile,
        imageSize: imageFile ? imageFile.size : 0
      });
      
      let requestBody;
      let config = {
        timeout: 600000 
      };
      
      if (imageFile) {
        const formData = new FormData();
        
        // Handle both array and single hookId
        if (Array.isArray(hookIds)) {
          // If single element array, append as single value
          if (hookIds.length === 1) {
            formData.append('sequenceId', hookIds[0]);
          } else {
            // Multiple elements - append each one
            hookIds.forEach((sequenceId) => {
              formData.append('sequenceId', sequenceId);
            });
          }
        } else {
          formData.append('sequenceId', hookIds);
        }
        
        formData.append('image', imageFile);
        
        requestBody = formData;
        
        // Log FormData contents
        console.log('📋 FormData contents:');
        for (let pair of formData.entries()) {
          console.log(`  ${pair[0]}:`, pair[1] instanceof File ? `File(${pair[1].name})` : pair[1]);
        }
      } else {
        requestBody = {
          sequenceId: hookIds
        };
        console.log('📋 JSON body:', requestBody);
      }
      
      console.log('⏳ Sending request...');
      const response = await apiInvoker.post(url, requestBody, config);
      console.log('✅ Received response:', response);
      
      if (response?.success && response?.data) {
        dispatch(generateScriptSuccess(response.data));
        console.log('✅ Script generated successfully:', response.data);
        return response.data;
      }
      if (response) {
        dispatch(generateScriptSuccess(response));
        console.log('✅ Script generated (direct response):', response);
        return response;
      }
      
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('❌ Generate script error:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        data: error.data
      });
      
      const { error: formattedError, message: errorMessage } = handleApiError(error, {
        defaultMessage: 'Failed to generate script',
        customMessages: {
          400: error.data?.error || 'Bad request. Please check your input.',
          413: 'Image file is too large. Please use a smaller image file.',
          500: 'Server error occurred while generating the script. Please try again later or contact support if the issue persists.',
          408: 'Request timed out. The script generation is taking longer than expected. Please try again.',
        },
      });
      
      dispatch(generateScriptFailure(errorMessage));
      throw formattedError;
    }
  };
};

export const deleteHook = (hookId) => {
  return async (dispatch) => {
    try {
      dispatch(deleteHookStart());
      
      const url = API_URLS.USER.DELETE_HOOK(hookId);
      const response = await apiInvoker.delete(url);
      
      if (response?.success) {
        dispatch(deleteHookSuccess(hookId));
        return { success: true, hookId };
      }
      
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Delete hook error:', error);
      
      const { error: formattedError, message: errorMessage } = handleApiError(error, {
        defaultMessage: 'Failed to delete hook',
        customMessages: {
          404: 'Hook not found',
          500: 'Server error occurred while deleting the hook. Please try again later.',
        },
      });
      
      dispatch(deleteHookFailure(errorMessage));
      throw formattedError;
    }
  };
};

export const updateHook = (hookId, data) => {
  return async (dispatch) => {
    try {
      dispatch(updateHookStart());
      
      const url = API_URLS.USER.UPDATE_HOOK(hookId);
      const requestBody = {};
      
      if (data.hook !== undefined) {
        requestBody.hook = { text: data.hook };
      }
      if (data.openLoop !== undefined) {
        requestBody.open_loop = { text: data.openLoop };
      }
      if (data.retentionMoment !== undefined) {
        requestBody.retention_moment = { text: data.retentionMoment };
      }
      if (data.ctaPlacement !== undefined) {
        requestBody.cta_placement = { text: data.ctaPlacement };
      }
      
      const response = await apiInvoker.put(url, requestBody);
      
      if (response?.success) {
        dispatch(updateHookSuccess({ hookId, data }));
        return { success: true, hookId, data };
      }
      
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Update hook error:', error);
      
      const { error: formattedError, message: errorMessage } = handleApiError(error, {
        defaultMessage: 'Failed to update hook',
        customMessages: {
          404: 'Hook not found',
          500: 'Server error occurred while updating the hook. Please try again later.',
        },
      });
      
      dispatch(updateHookFailure(errorMessage));
      throw formattedError;
    }
  };
};

export { clearResearchReport };

